import { strtotime } from "locutus/php/datetime";
import { extend, localize, ValidationObserver, ValidationProvider } from "vee-validate";
import en from "vee-validate/dist/locale/en.json";
import fr from "vee-validate/dist/locale/fr.json";
import * as rules from "vee-validate/dist/rules";
import dayjs from "./helpers/dayjs";
import i18n from "./i18n";

// i18n locales are en or fr. Let's be more precise for currency and time formats.
const preciseLocale = { fr: "fr-CA", en: "en-CA" }[i18n.locale];

// Install VeeValidate rules and localization
Object.keys(rules).forEach((rule) => {
  extend(rule, (rules as any)[rule]);
});

extend("url", {
  message: (field) => i18n.t("vee_validate.messages.url", { field }) as string,
  validate: (value) => {
    try {
      new URL(value);
      return true;
    } catch (e) {
      return false;
    }
  },
});
extend("nullable", () => true); // More of a backend concern, just skip through
extend("date", (v) => {
  const parsedDate = new Date(v);
  return !Number.isNaN(parsedDate.getTime());
});
extend("present", (v) => v !== null && v !== undefined);
extend("boolean", (v) => typeof v === "boolean");
extend("accepted", {
  validate: (v) => v === true,
  message: () => i18n.t("vee_validate.messages.accepted") as string,
});
extend("before", {
  validate: (v, args) => {
    const parsedDate = new Date(strtotime((args as any[])[0]) * 1000);

    if (!Number.isNaN(parsedDate.getTime())) {
      return new Date(v) <= parsedDate;
    }

    return false;
  },
  message: (field, args) => {
    const parsedDate = new Date(strtotime((args as any[])[0]) * 1000);
    return i18n.t("vee_validate.messages.before", {
      field,
      date: dayjs(parsedDate).format("DD MMMM YYYY"),
    }) as string;
  },
});
extend("after", {
  validate: (v, args) => {
    const parsedDate = new Date(strtotime((args as any[])[0]) * 1000);

    if (!Number.isNaN(parsedDate.getTime())) {
      return new Date(v) >= parsedDate;
    }

    return false;
  },
  message: (field, args) => {
    const parsedDate = new Date(strtotime((args as any[])[0]) * 1000);
    return i18n.t("vee_validate.messages.after", {
      field,
      date: dayjs(parsedDate).format("DD MMMM YYYY"),
    }) as string;
  },
});
extend("is", {
  message: (field) => i18n.t("vee_validate.messages.is", { field }) as string,
});

extend("currency_max_value", {
  validate: (v, args) => {
    return Number.parseFloat(v.replace(/ $| /g, "").replace(",", ".")) <= (args as any[])[0];
  },
  message: (field, args) => {
    return i18n.t("vee_validate.messages.currency_max_value", {
      field,
      amount: (args as any[])[0].toFixed(2).toLocaleString(preciseLocale),
    }) as string;
  },
});
extend("currency_min_value", {
  validate: (v, args) => {
    return Number.parseFloat(v.replace(/ $| /g, "").replace(",", ".")) >= (args as any[])[0];
  },
  message: (field, args) => {
    return i18n.t("vee_validate.messages.currency_min_value", {
      field,
      amount: (args as any[])[0].toFixed(2).toLocaleString(preciseLocale),
    }) as string;
  },
});

// Install locales en and fr.
localize({ en, fr });
// Set the locale.
localize(i18n.locale);

export { ValidationObserver, ValidationProvider };
